import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  GRADE,
  GRADE_COLORS,
} from 'views/IssueDetails/components/AIAnalysis/constants';

// Constants for Japanese text
const JAPANESE_TEXT = {
  NO_GRADE: '判定なし',
  NO_COMMENT: 'コメントなし',
  NO_ITEM: 'N/A',
};

const AttachmentSections = ({ attachments, onItemClick }) => {
  // Helper function to extract letter grade (ignoring digits)
  const getLetterGrade = (grade) => {
    if (!grade || typeof grade !== 'string') return null;
    const match = grade.match(/^([A-Z])/i);
    return match ? match[1].toUpperCase() : null;
  };

  // Helper function to get colors for a grade
  // Falls back to DEFAULT colors for unknown grades (F, G, I, J, K, L, M, N, etc.)
  const getGradeColors = (grade) => {
    const letterGrade = getLetterGrade(grade);
    return GRADE_COLORS[letterGrade] || GRADE_COLORS.DEFAULT;
  };

  // Separate items into different categories
  const normalItems = [];
  const aGradeItems = [];
  const bGradeItems = [];
  const noGradeItems = [];

  attachments.forEach((item) => {
    const { result } = item;
    const grade = result.answer.grade;
    const letterGrade = getLetterGrade(grade);

    // Check if item has no grade
    const isNoGradeItem = !letterGrade;

    // Check if item has A grade
    const isAGradeItem = letterGrade === GRADE.A;

    // Check if item has B grade
    const isBGradeItem = letterGrade === GRADE.B;

    if (isNoGradeItem) {
      noGradeItems.push(item);
    } else if (isAGradeItem) {
      aGradeItems.push(item);
    } else if (isBGradeItem) {
      bGradeItems.push(item);
    } else {
      // All other grades (C, D, E, H, and any unknown grades like F, G, I, J, K, L, M, N, etc.)
      // are displayed individually for visibility
      normalItems.push(item);
    }
  });

  const renderNormalItem = (item) => {
    const { result, id } = item;
    const colors = getGradeColors(result.answer.grade);

    return (
      <div
        key={id}
        id={`left-item-${id}`}
        onClick={() => onItemClick && onItemClick(id)}
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: colors.background,
          cursor: 'pointer',
        }}>
        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
          項目: {result.answer.item || JAPANESE_TEXT.NO_ITEM}
        </div>
        <div
          style={{
            marginBottom: '8px',
            fontWeight: 'bold',
            color: colors.text,
          }}>
          判定: {result.answer.grade || JAPANESE_TEXT.NO_GRADE}
        </div>
        <div style={{ lineHeight: '1.5' }}>
          {result.answer.original_comment || JAPANESE_TEXT.NO_COMMENT}
        </div>
      </div>
    );
  };

  const renderCompactItem = (item) => {
    const { result, id } = item;
    const colors = getGradeColors(result.answer.grade);

    return (
      <div
        key={id}
        id={`left-item-${id}`}
        onClick={() => onItemClick && onItemClick(id)}
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          padding: '12px',
          marginBottom: '8px',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
        }}>
        <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
          項目: {result.answer.item || JAPANESE_TEXT.NO_ITEM}
        </div>
        <div
          style={{
            marginBottom: '4px',
            fontWeight: 'bold',
            color: colors.text,
          }}>
          判定: {result.answer.grade || JAPANESE_TEXT.NO_GRADE}
        </div>
        <div style={{ lineHeight: '1.5' }}>
          {result.answer.original_comment || JAPANESE_TEXT.NO_COMMENT}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '0 16px' }}>
      {/* Display normal items individually */}
      {normalItems.map(renderNormalItem)}

      {/* Display B-grade items in a collapsible accordion */}
      {bGradeItems.length > 0 && (
        <Accordion
          style={{
            marginBottom: '16px',
            border: `1px solid ${GRADE_COLORS.B.border}`,
            borderRadius: '8px',
            backgroundColor: GRADE_COLORS.B.accordionBg,
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="b-grade-content"
            id="b-grade-header"
            style={{
              backgroundColor: GRADE_COLORS.B.accordionHeaderBg,
              borderRadius: '8px 8px 0 0',
            }}>
            <div style={{ fontWeight: 'bold' }}>
              要経過観察項目 (B判定・{bGradeItems.length}件)
            </div>
          </AccordionSummary>
          <AccordionDetails style={{ padding: '8px' }}>
            {bGradeItems.map(renderCompactItem)}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Display A-grade items in a collapsible accordion */}
      {aGradeItems.length > 0 && (
        <Accordion
          style={{
            marginBottom: '16px',
            border: `1px solid ${GRADE_COLORS.A.border}`,
            borderRadius: '8px',
            backgroundColor: GRADE_COLORS.A.accordionBg,
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="a-grade-content"
            id="a-grade-header"
            style={{
              backgroundColor: GRADE_COLORS.A.accordionHeaderBg,
              borderRadius: '8px 8px 0 0',
            }}>
            <div style={{ fontWeight: 'bold' }}>
              正常項目 (A判定・{aGradeItems.length}件)
            </div>
          </AccordionSummary>
          <AccordionDetails style={{ padding: '8px' }}>
            {aGradeItems.map(renderCompactItem)}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Display No Grade items in a collapsible accordion */}
      {noGradeItems.length > 0 && (
        <Accordion
          style={{
            marginBottom: '16px',
            border: `1px solid ${GRADE_COLORS.DEFAULT.border}`,
            borderRadius: '8px',
            backgroundColor: GRADE_COLORS.DEFAULT.accordionBg,
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="no-grade-content"
            id="no-grade-header"
            style={{
              backgroundColor: GRADE_COLORS.DEFAULT.accordionHeaderBg,
              borderRadius: '8px 8px 0 0',
            }}>
            <div style={{ fontWeight: 'bold' }}>
              判定なし項目 ({noGradeItems.length}件)
            </div>
          </AccordionSummary>
          <AccordionDetails style={{ padding: '8px' }}>
            {noGradeItems.map(renderCompactItem)}
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

AttachmentSections.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.object).isRequired,
  onItemClick: PropTypes.func,
};

export default AttachmentSections;
